import { NgModule, inject } from '@angular/core';
import { CanActivateFn, Router, RouterModule, Routes } from '@angular/router';
import { AuthGuard } from './core/guards/auth.guard';
import { AuthService } from './core/service/auth/auth.service';
import { permissionGuard } from './core/guards/permission.guard';
import { MainLayoutComponent } from './shared/components/main-layout/main-layout.component';
import { FederationShellComponent } from './shared/components/federation-shell/federation-shell.component';
import { UnauthorizedComponent } from './pages/unauthorized/unauthorized.component';
import { HistoryEventsListComponent } from './pages/Management/history-events/history-events-list.component';
import { IntInstancesListComponent } from './pages/Management/int-instances/int-instances-list.component';
import { DocumentsPublicComponent } from './pages/Management/documents-public/documents-public.component';
import { ActualitesListComponent } from './pages/Communication/actualites/actualites-list/actualites-list.component';
import { ActualitesFormComponent } from './pages/Communication/actualites/actualites-form/actualites-form.component';
import { ActualitesDetailComponent } from './pages/Communication/actualites/actualites-detail/actualites-detail.component';
import { CommuniquesListComponent } from './pages/Communication/communiques/communiques-list/communiques-list.component';
import { CommuniquesFormComponent } from './pages/Communication/communiques/communiques-form/communiques-form.component';
import { CommuniquesDetailComponent } from './pages/Communication/communiques/communiques-detail/communiques-detail.component';
import { RevuesListComponent } from './pages/Communication/revues-presse/revues-list/revues-list.component';
import { RevuesFormComponent } from './pages/Communication/revues-presse/revues-form/revues-form.component';
import { RevuesDetailComponent } from './pages/Communication/revues-presse/revues-detail/revues-detail.component';
import { FormulairesListComponent } from './pages/Communication/formulaires/formulaires-list/formulaires-list.component';
import { FormulairesFormComponent } from './pages/Communication/formulaires/formulaires-form/formulaires-form.component';
import { FormulairesDetailComponent } from './pages/Communication/formulaires/formulaires-detail/formulaires-detail.component';
import { MediatiqueListComponent } from './pages/Communication/mediatique/mediatique-list/mediatique-list.component';
import { MediatiqueDetailComponent } from './pages/Communication/mediatique/mediatique-detail/mediatique-detail.component';
import { MediatiqueFormComponent } from './pages/Communication/mediatique/mediatique-form/mediatique-form.component';
import { PartenairesComponent } from './pages/Communication/partenaires/partenaires.component';
import { DocumentsPublicDetailComponent } from './pages/Management/documents-public/documents-public-detail/documents-public-detail.component';
import { MeetingsListComponent } from './pages/Communication/meetings/meetings-list/meetings-list.component';
import { MeetingsFormComponent } from './pages/Communication/meetings/meetings-form/meetings-form.component';
import { MeetingsDetailComponent } from './pages/Communication/meetings/meetings-detail/meetings-detail.component';
import { SocialMediaListComponent } from './pages/Communication/social-media/social-media-list/social-media-list.component';
import { SocialMediaFormComponent } from './pages/Communication/social-media/social-media-form/social-media-form.component';

const redirectIfAuthenticated: CanActivateFn = () => {
  const authService = inject(AuthService);
  const router = inject(Router);
  if (authService.isAuthenticated()) {
    router.navigate(['/federation/actualites']);
    return false;
  }
  return true;
};

export const routes: Routes = [
  {
    path: '',
    component: FederationShellComponent,
    children: [
      // Login under /federation/login
      {
        path: 'federation/login',
        canActivate: [redirectIfAuthenticated],
        loadChildren: () =>
          import('./pages/auth/auth.module').then(m => m.AuthModule),
      },
      // Protected federation routes
      {
        path: 'federation',
        component: MainLayoutComponent,
        canActivate: [AuthGuard],
        children: [
          {
            path: 'actualites',
            canActivate: [permissionGuard('marketing', 'actualites', 'read')],
            children: [
              { path: '', component: ActualitesListComponent },
              { path: 'new', component: ActualitesFormComponent, canActivate: [permissionGuard('marketing', 'actualites', 'create')] },
              { path: ':id', component: ActualitesDetailComponent },
              { path: ':id/edit', component: ActualitesFormComponent, canActivate: [permissionGuard('marketing', 'actualites', 'edit')] }
            ]
          },
          {
            path: 'communiques',
            children: [
              { path: '', component: CommuniquesListComponent },
              { path: 'new', component: CommuniquesFormComponent },
              { path: ':id', component: CommuniquesDetailComponent },
              { path: ':id/edit', component: CommuniquesFormComponent }
            ]
          },
          {
            path: 'revues-presse',
            children: [
              { path: '', component: RevuesListComponent },
              { path: 'new', component: RevuesFormComponent },
              { path: ':id', component: RevuesDetailComponent },
              { path: ':id/edit', component: RevuesFormComponent }
            ]
          },
          {
            path: 'content-manager',
            canActivate: [permissionGuard('marketing', 'pages', 'read')],
            loadChildren: () =>
              import('./pages/ContentManager/content-manager.module').then(m => m.ContentManagerModule)
          },
          {
            path: 'formulaires',
            children: [
              { path: '', component: FormulairesListComponent },
              { path: 'new', component: FormulairesFormComponent },
              { path: ':id', component: FormulairesDetailComponent },
            ]
          },
          {
            path: 'mediatique',
            canActivate: [permissionGuard('marketing', 'albums', 'read')],
            children: [
              { path: '', component: MediatiqueListComponent },
              { path: 'new', component: MediatiqueFormComponent, canActivate: [permissionGuard('marketing', 'albums', 'create')] },
              { path: ':id', component: MediatiqueDetailComponent },
              { path: ':id/edit', component: MediatiqueDetailComponent, canActivate: [permissionGuard('marketing', 'albums', 'edit')] },
            ]
          },
          {
            path: 'reunions',
            children: [
              { path: '', component: MeetingsListComponent },
              { path: 'new', component: MeetingsFormComponent },
              { path: ':id', component: MeetingsDetailComponent },
              { path: ':id/edit', component: MeetingsFormComponent }
            ]
          },
          {
            path: 'reseaux-sociaux',
            canActivate: [permissionGuard('marketing', 'reseaux_sociaux', 'read')],
            children: [
              { path: '', component: SocialMediaListComponent },
              { path: 'new', component: SocialMediaFormComponent, canActivate: [permissionGuard('marketing', 'reseaux_sociaux', 'create')] },
              { path: ':id/edit', component: SocialMediaFormComponent, canActivate: [permissionGuard('marketing', 'reseaux_sociaux', 'edit')] }
            ]
          },
          { path: 'partenaires', component: PartenairesComponent, canActivate: [permissionGuard('marketing', 'partenaires', 'read')] },
          { path: 'evenements-historiques', component: HistoryEventsListComponent },
          { path: 'instances-internationales', component: IntInstancesListComponent },
          {
            path: 'members',
            canActivate: [permissionGuard('marketing', 'members', 'read')],
            loadChildren: () =>
              import('./pages/Management/members/members.module').then(m => m.MembersModule)
          },
          {
            path: 'bureau-executif',
            canActivate: [permissionGuard('marketing', 'bureau_executif', 'read')],
            loadChildren: () =>
              import('./pages/Management/bureau-executif/bureau-executif.module').then(m => m.BureauExecutifModule)
          },
          {
            path: 'administration',
            canActivate: [permissionGuard('marketing', 'administration', 'read')],
            loadChildren: () =>
              import('./pages/Management/administration/administration.module').then(m => m.AdministrationModule)
          },
          {
            path: 'documents-publics',
            canActivate: [permissionGuard('marketing', 'documents', 'read')],
            children: [
              { path: '', component: DocumentsPublicComponent },
              { path: ':id', component: DocumentsPublicDetailComponent },
              { path: ':id/edit', component: DocumentsPublicComponent, canActivate: [permissionGuard('marketing', 'documents', 'edit')] }
            ]
          },
          { path: '', redirectTo: 'actualites', pathMatch: 'full' }
        ]
      },
      // Error pages
      { path: 'unauthorized', component: UnauthorizedComponent },
      { path: 'no-access', component: UnauthorizedComponent },

      { path: '', redirectTo: '/federation/login', pathMatch: 'full' },
      { path: '**', redirectTo: '/federation/login' },
    ]
  }
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
